import React, { FC, useEffect } from 'react';
import { useQueryClient } from 'react-query';

import {
  ACCESS_KEY,
  CONNECTION_SUCCESS_MESSAGE,
  NULL,
  SHARE_LOADING_MESSAGE,
  SHARE_SUCCESS_MESSAGE,
} from 'app-constants';
import {
  ListPublicSocialConnectionsQuery,
  ShareType,
  useListPublicSocialConnectionsQuery,
  usePublicShareContentMutation,
} from 'api/public';
import { useNotify } from 'hooks';
import PublicShareErrorMessage from 'features/content/partials/PublicShareErrorMessage';
import PublicShareSuccessMessage from 'features/content/partials/PublicShareSuccessMessage';

const PublicConnections: FC = () => {
  const queryClient = useQueryClient();
  const notify = useNotify();

  function onShareError() {
    notify.error({ component: <PublicShareErrorMessage /> });
  }

  const { mutate } = usePublicShareContentMutation({
    onError: onShareError,
  });

  useEffect(() => {
    window.addEventListener('message', event => void updateConnections(event), false);
    return () => {
      window.removeEventListener('message', event => void updateConnections(event), false);
    };
  });

  async function updateConnections(event: MessageEvent) {
    if (!event.data || event.data.message !== CONNECTION_SUCCESS_MESSAGE) {
      return;
    }

    const { network, content_id, messageText } = event.data;

    const access_key = localStorage.getItem(ACCESS_KEY);

    window.postMessage({ message: SHARE_LOADING_MESSAGE, network, content_id }, '*');

    const data: ListPublicSocialConnectionsQuery = await queryClient.fetchQuery(
      useListPublicSocialConnectionsQuery.getKey({ access_key })
    );

    const items = data?.listPublicSocialConnections?.items || [];

    const userNetworkInfo = items.find(item => item.provider === network);

    function onShareSuccess() {
      window.postMessage(
        {
          message: SHARE_SUCCESS_MESSAGE,
          network,
          content_id,
        },
        '*'
      );
      notify.info({ component: <PublicShareSuccessMessage network={network} />, duration: 8000 });
    }

    mutate(
      {
        content_id,
        access_key,
        items: [
          {
            network_id: userNetworkInfo.id,
            user_commentary: messageText,
            share_type: ShareType.Now,
          },
        ],
      },
      { onSuccess: onShareSuccess }
    );
  }

  return NULL;
};

export default PublicConnections;
